// Team Pulse — the owner's meeting screen: who is holding what, what has
// slipped, who is delivering. One row per person; each row prints to a
// one-page staff meeting sheet.
import { useEffect, useState } from "react";
import { Printer, X, AlertTriangle, Trophy, RefreshCw } from "lucide-react";
import { api } from "./api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

export default function TeamPulse() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sheetMember, setSheetMember] = useState(null);

  const load = () => {
    api.get("/team/pulse")
      .then((r) => { setData(r.data); setError(null); })
      .catch(() => setError("Couldn't load the team pulse. Please try again."));
  };
  useEffect(load, []);

  if (error) {
    return (
      <div className="p-6 text-sm text-red-700" data-testid="pulse-error">{error}</div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-zinc-500" data-testid="pulse-loading">Loading team pulse…</div>;
  }

  const showUnassigned = data.unassigned && data.unassigned.open_total > 0;

  return (
    <div className="px-3 py-5 sm:px-6" data-testid="team-pulse">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-zinc-900">Team pulse</h1>
          <p className="text-sm text-zinc-500">
            Who is holding what, what's slipped, and wins for {data.month_label}.
          </p>
        </div>
        <button
          type="button"
          data-testid="pulse-refresh"
          onClick={load}
          className="flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-sm" data-testid="pulse-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-semibold">Person</th>
              {data.columns.map((c) => (
                <th key={c} className="px-3 py-3 text-center font-semibold">{c}</th>
              ))}
              <th className="px-3 py-3 text-center font-semibold text-red-600">Overdue</th>
              <th className="px-3 py-3 text-center font-semibold">Due today</th>
              <th className="px-3 py-3 text-center font-semibold">Wins</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.member_id} data-testid={`pulse-row-${m.member_id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60">
                <td className="px-4 py-3">
                  <div className="font-semibold text-zinc-900">{m.name}</div>
                  <div className="text-xs text-zinc-500">
                    {m.role === "staff" ? "Staff" : "Owner/Admin"}
                    {m.designation ? ` · ${m.designation}` : ""}
                    {` · ${m.open_total} open`}
                  </div>
                </td>
                {data.columns.map((c) => (
                  <td key={c} className="px-3 py-3 text-center tabular-nums text-zinc-700">
                    {m.by_stage[c] || <span className="text-zinc-300">·</span>}
                  </td>
                ))}
                <td className="px-3 py-3 text-center">
                  {m.overdue_count > 0 ? (
                    <span data-testid={`pulse-overdue-${m.member_id}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">
                      <AlertTriangle size={11} /> {m.overdue_count}
                    </span>
                  ) : (
                    <span className="text-zinc-300">·</span>
                  )}
                </td>
                <td className="px-3 py-3 text-center tabular-nums text-zinc-700">
                  {m.due_today_count || <span className="text-zinc-300">·</span>}
                </td>
                <td className="px-3 py-3 text-center">
                  {m.wins_month > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">
                      <Trophy size={11} /> {m.wins_month}
                    </span>
                  ) : (
                    <span className="text-zinc-300">·</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    type="button"
                    data-testid={`pulse-print-btn-${m.member_id}`}
                    onClick={() => setSheetMember(m)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    <Printer size={13} /> Meeting sheet
                  </button>
                </td>
              </tr>
            ))}
            {showUnassigned && (
              <tr data-testid="pulse-row-unassigned" className="border-t border-zinc-200 bg-amber-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-amber-800">Not assigned to anyone</div>
                  <div className="text-xs text-amber-700">{data.unassigned.open_total} open — give these a handler</div>
                </td>
                {data.columns.map((c) => (
                  <td key={c} className="px-3 py-3 text-center tabular-nums text-amber-800">
                    {data.unassigned.by_stage[c] || <span className="text-amber-300">·</span>}
                  </td>
                ))}
                <td className="px-3 py-3 text-center">
                  {data.unassigned.overdue_count > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">
                      <AlertTriangle size={11} /> {data.unassigned.overdue_count}
                    </span>
                  ) : <span className="text-amber-300">·</span>}
                </td>
                <td className="px-3 py-3 text-center text-amber-800">{data.unassigned.due_today_count || "·"}</td>
                <td className="px-3 py-3 text-center text-amber-800">·</td>
                <td className="px-3 py-3" />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-zinc-400">
        Numbers count open enquiries only. Overdue = the follow-up date has passed. Wins = marked as won this month.
      </p>

      {sheetMember && (
        <MeetingSheet member={sheetMember} onClose={() => setSheetMember(null)} />
      )}
    </div>
  );
}

const MeetingSheet = ({ member, onClose }) => {
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/team/pulse/${member.member_id}/sheet`)
      .then((r) => setSheet(r.data))
      .catch(() => setError("Couldn't prepare the sheet. Please try again."));
  }, [member.member_id]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/50" data-testid="pulse-sheet">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .pulse-print-area, .pulse-print-area * { visibility: visible !important; }
          .pulse-print-area { position: absolute !important; inset: 0 !important; overflow: visible !important; max-height: none !important; box-shadow: none !important; border-radius: 0 !important; }
          .pulse-no-print { display: none !important; }
        }
      `}</style>
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col p-3 sm:p-6">
        <div className="pulse-no-print mb-2 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="pulse-sheet-print"
            onClick={() => window.print()}
            disabled={!sheet}
            className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          >
            <Printer size={15} /> Print / Save PDF
          </button>
          <button
            type="button"
            data-testid="pulse-sheet-close"
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
          >
            <X size={15} /> Close
          </button>
        </div>

        <div className="pulse-print-area flex-1 overflow-y-auto rounded-xl bg-white p-6 shadow-xl sm:p-8">
          {error ? (
            <div className="text-sm text-red-700">{error}</div>
          ) : !sheet ? (
            <div className="text-sm text-zinc-500">Preparing the sheet…</div>
          ) : (
            <div data-testid="pulse-sheet-body">
              <div className="flex flex-wrap items-start justify-between gap-2 border-b-2 border-zinc-900 pb-3">
                <div>
                  <div className="text-lg font-bold text-zinc-900">{sheet.member.name}</div>
                  <div className="text-xs text-zinc-500">
                    {sheet.member.role === "staff" ? "Staff" : "Owner/Admin"}
                    {sheet.member.designation ? ` · ${sheet.member.designation}` : ""}
                    {` — ${sheet.org_name}`}
                  </div>
                </div>
                <div className="text-right text-xs text-zinc-500">
                  <div className="font-semibold text-zinc-700">Staff meeting sheet</div>
                  <div>{sheet.date_label}</div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span><strong>{sheet.open_items.length}</strong> open</span>
                <span className={sheet.overdue_count > 0 ? "font-bold text-red-700" : ""}>
                  <strong>{sheet.overdue_count}</strong> overdue
                </span>
                <span><strong>{sheet.due_today_count}</strong> due today</span>
                <span><strong>{sheet.wins_count}</strong> won in {sheet.month_label}</span>
              </div>

              <h2 className="mt-5 border-b border-zinc-300 pb-1 text-sm font-bold uppercase tracking-wide text-zinc-700">
                Pending enquiries
              </h2>
              {sheet.open_items.length === 0 ? (
                <p className="mt-2 text-sm text-zinc-500">Nothing pending. Board is clear.</p>
              ) : (
                <table className="mt-2 w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-wide text-zinc-500">
                      <th className="py-1.5 pr-2 font-semibold">Customer</th>
                      <th className="py-1.5 pr-2 font-semibold">Phone</th>
                      <th className="py-1.5 pr-2 font-semibold">Stage</th>
                      <th className="py-1.5 pr-2 font-semibold">Follow up</th>
                      <th className="py-1.5 font-semibold">Last note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.open_items.map((it, i) => (
                      <tr key={i} className="border-b border-zinc-100 align-top">
                        <td className="py-1.5 pr-2 font-semibold text-zinc-900">{it.name}</td>
                        <td className="py-1.5 pr-2 whitespace-nowrap text-zinc-700">{it.phone}</td>
                        <td className="py-1.5 pr-2 text-zinc-700">{it.stage_label}</td>
                        <td className="py-1.5 pr-2 whitespace-nowrap">
                          {it.days_overdue > 0 ? (
                            <span className="font-bold text-red-700">
                              {fmtDate(it.next_followup_date)} · {it.days_overdue}d late
                            </span>
                          ) : (
                            <span className="text-zinc-700">{fmtDate(it.next_followup_date)}</span>
                          )}
                        </td>
                        <td className="py-1.5 text-zinc-500">{it.last_remark || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h2 className="mt-5 border-b border-zinc-300 pb-1 text-sm font-bold uppercase tracking-wide text-zinc-700">
                Won in {sheet.month_label}
              </h2>
              {sheet.wins.length === 0 ? (
                <p className="mt-2 text-sm text-zinc-500">No wins recorded yet this month.</p>
              ) : (
                <table className="mt-2 w-full text-xs">
                  <tbody>
                    {sheet.wins.map((w, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="py-1.5 pr-2 font-semibold text-zinc-900">{w.name}</td>
                        <td className="py-1.5 pr-2 text-zinc-700">{w.phone}</td>
                        <td className="py-1.5 text-right text-zinc-500">won {fmtDate(w.won_on)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <p className="mt-6 text-[10px] text-zinc-400">
                Printed from Jigyasa · {sheet.org_name} · {sheet.date_label}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
